import type {
    ElementType,
    Options as EditableOption,
} from '@highcharts/dashboards/es-modules/Dashboards/Components/EditableOptions.js';
import {
    array,
    catch as zodCatch,
    enum as zodEnum,
    object,
    pipe,
    record,
    registry,
    string,
    transform,
    unknown as zodUnknown,
} from 'zod/mini';
import type { infer as Infer, ZodMiniType } from 'zod/mini';
import { isRecord } from '@h20/contracts/utils/object';
import type { DashboardWidgetSettingsContext } from './dashboard-widget-registry';

/** What the settings panel can show in a single field. */
export type SettingsFieldValue = string | number | boolean;

/** Whether the settings panel can show this value. */
export function isSettingsFieldValue(value: unknown): value is SettingsFieldValue {
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

/** What a widget says about a field so the settings panel can draw it */
export type DashboardWidgetFieldMeta = {
    /** i18n key for the label. Without one the panel shows the field's key in the schema. */
    label?: string;
    /** Fills a dropdown from the bound query */
    choices?: (context: DashboardWidgetSettingsContext) => readonly string[];
    inline?: boolean;
    /** Draws a text input as a textarea. */
    multiline?: boolean;
};

/** Every field's panel entry, keyed by the schema value it was attached to. */
const fieldMeta = registry<DashboardWidgetFieldMeta>();

/** What `repeated` records so the panel can draw the count dropdown and one group per item. */
type RepeatedMeta = {
    readonly item: ZodMiniType;
    /** Where each item is stored, `c1` upward. */
    readonly keys: readonly string[];
    /** What the count dropdown offers, `1` upward, one per key. */
    readonly counts: readonly string[];
    /** How many groups to draw when the saved config has no count. */
    readonly fallbackCount: number;
    readonly itemLabel: string;
    readonly countLabel: string;
};

const repeatedMeta = new WeakMap<ZodMiniType, RepeatedMeta>();

/** This schema, plus how the settings panel draws the field holding it */
export function describeField<T extends ZodMiniType>(
    schema: T,
    field: DashboardWidgetFieldMeta
): T {
    const widened: ZodMiniType = schema;
    widened.register(fieldMeta, field);
    return schema;
}

/**
 * The same schema with a fallback on every field.
 *
 * The fallback comes from the field's type: text becomes an empty string, a switch becomes `false`, a
 * number becomes `0`, and a dropdown becomes the first value in its list. Where that guess is wrong,
 * write the fallback yourself with `zodCatch()` and this leaves it alone.
 */
export function withDefaults<T extends ZodMiniType>(schema: T): ZodMiniType<Infer<T>, unknown>;
export function withDefaults(schema: ZodMiniType): ZodMiniType {
    const def = schemaDef(schema);
    // A `catch` is a fallback somebody chose on purpose
    // and a `pipe` is a `repeated` group that already fills its own gaps.
    if (def.type === 'catch' || def.type === 'pipe') {
        return schema;
    }

    let rebuilt: ZodMiniType;
    if (def.shape !== undefined) {
        const fields = object(
            Object.fromEntries(
                Object.entries(def.shape).map(([key, field]) => [key, withDefaults(field)])
            )
        );
        rebuilt = zodCatch(fields, () => fields.parse({}));
    } else if (def.valueType !== undefined) {
        rebuilt = zodCatch(record(string(), withDefaults(def.valueType)), () => ({}));
    } else if (def.element !== undefined) {
        rebuilt = zodCatch(array(withDefaults(def.element)), () => []);
    } else {
        let fallback: string | number | boolean = '';
        if (def.entries !== undefined) {
            fallback = Object.values(def.entries)[0] ?? '';
        } else if (def.type === 'boolean') {
            fallback = false;
        } else if (def.type === 'number') {
            fallback = 0;
        }
        rebuilt = zodCatch(schema, fallback);
    }

    // The panel's entries are keyed by the schema value, so a rebuilt field would lose its label.
    const meta = fieldMeta.get(schema);
    return meta === undefined ? rebuilt : rebuilt.register(fieldMeta, meta);
}

/** How many of a repeated group the author asked for, and the groups themselves. */
type RepeatedGroup<TItem> = {
    readonly count: string;
    readonly items: Readonly<Record<string, TItem>>;
};

/** What a widget says about a group it wants several of. */
export type RepeatedOptions = {
    /** The most the author may ask for. The count dropdown offers 1 up to this. */
    readonly max: number;
    /** How many a config that never named a count gets. */
    readonly fallback: number;
    /** i18n key for the group holding them all. */
    readonly label: string;
    /** i18n key for each one. The panel numbers them by position, from 1. */
    readonly itemLabel: string;
    /** i18n key for the count dropdown. */
    readonly countLabel: string;
};

/** One shape, repeated as many times as the author asks for. */
export function repeated<T extends ZodMiniType>(
    item: T,
    options: RepeatedOptions
): ZodMiniType<RepeatedGroup<Infer<T>>, unknown> {
    const counts = Array.from({ length: options.max }, (_, index) => String(index + 1));
    const keys = counts.map((count) => `c${count}`);
    const itemWithDefaults = withDefaults(item);

    const stored = withDefaults(
        object({
            count: zodCatch(zodEnum(counts), String(options.fallback)),
            items: zodCatch(record(string(), zodUnknown()), () => ({})),
        })
    );

    const group = pipe(
        stored,
        transform((value) => ({
            count: value.count,
            items: Object.fromEntries(
                keys
                    .slice(0, Number(value.count))
                    .map((key) => [key, itemWithDefaults.parse(value.items[key])])
            ),
        }))
    );

    describeField(group, { label: options.label });
    repeatedMeta.set(group, {
        item: itemWithDefaults,
        keys,
        counts,
        fallbackCount: options.fallback,
        itemLabel: options.itemLabel,
        countLabel: options.countLabel,
    });
    return group;
}

/** One entry per column, keyed by the column's `key`. */
type ColumnSettings<TItem> = Readonly<Record<string, TItem>>;

/**
 * A column as a widget shows it, with `key` naming where `perColumn` stores its settings. Most
 * widgets show the query's columns as they are. A widget that shows a different set lists its own.
 */
export type DashboardWidgetColumn = {
    readonly key: string;
    readonly label: string;
    /** What this column's fields show while nothing is saved, by field name. */
    readonly defaults?: Readonly<Record<string, SettingsFieldValue>>;
};

/** What `perColumn` records so the panel can spot one and draw an entry per column. */
type PerColumnMeta = {
    readonly item: ZodMiniType;
    readonly columns: (context: DashboardWidgetSettingsContext) => readonly DashboardWidgetColumn[];
};

const perColumnMeta = new WeakMap<ZodMiniType, PerColumnMeta>();

/** One copy of a schema per column, whose default must be one the panel draws as empty. */
export function perColumn<T extends ZodMiniType>(
    item: T,
    label: string,
    columns: (context: DashboardWidgetSettingsContext) => readonly DashboardWidgetColumn[] = (
        context
    ) => context.columns
): ZodMiniType<ColumnSettings<Infer<T>>, unknown> {
    const itemWithDefaults = withDefaults(item);
    const columnSettings = zodCatch(record(string(), itemWithDefaults), () => ({}));
    describeField(columnSettings, { label });
    perColumnMeta.set(columnSettings, { item: itemWithDefaults, columns });
    return columnSettings;
}

/**
 * How many stored settings the schema refused. A field the author never set, or cleared, is a gap
 * rather than a refusal: the default is the right answer for it and it is not counted.
 */
export function repairedConfigCount(stored: unknown, parsed: unknown): number {
    if (!isRecord(parsed)) {
        return 0;
    }
    if (!isRecord(stored)) {
        return isUnset(stored) ? 0 : 1;
    }
    return Object.entries(parsed).reduce((count, [key, value]) => {
        const before = stored[key];
        if (isUnset(before)) {
            return count;
        }
        if (isRecord(value)) {
            return count + repairedConfigCount(before, value);
        }
        // Compared as text, so a count hand written as the number 3 still matches the stored "3".
        return String(before) === String(value) ? count : count + 1;
    }, 0);
}

/** Whether a stored value carries no information, so a default replacing it loses nothing. */
function isUnset(value: unknown): boolean {
    return (
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
    );
}

type SchemaDef = {
    readonly type: string;
    /** An object's fields. Only an object schema has this. */
    readonly shape?: Readonly<Record<string, ZodMiniType>>;
    /** What a wrapper such as `catch` wraps. */
    readonly innerType?: ZodMiniType;
    /** A dropdown's values, keyed by themselves. */
    readonly entries?: Readonly<Record<string, string>>;
    /** What each value of a record holds. Only a record schema has this. */
    readonly valueType?: ZodMiniType;
    /** What each entry of a list holds. Only an array schema has this. */
    readonly element?: ZodMiniType;
};

function schemaDef(schema: ZodMiniType): SchemaDef {
    return schema.def as unknown as SchemaDef;
}

/** A widget's settings fields, plus what the panel needs to show each one. */
export type SettingsFields = {
    readonly fields: EditableOption[];
    /** What each dropdown offers, by dotted path, so the panel can catch an old saved value. */
    readonly choices: ReadonlyMap<string, readonly string[]>;
    readonly defaults: ReadonlyMap<string, SettingsFieldValue>;
};

/** What a field building function may read, and where it puts what it cannot return. */
type FieldsContext = {
    /** The cell being set up: its saved config, the query it is bound to, and that query's columns. */
    readonly cell: DashboardWidgetSettingsContext;
    /** Lookups the panel needs later, filled in as fields are built */
    readonly output: {
        readonly choices: Map<string, readonly string[]>;
        readonly defaults: Map<string, SettingsFieldValue>;
    };
};

/** A widget's settings fields, read off its config schema */
export function fieldsFromSchema(
    schema: ZodMiniType,
    context: DashboardWidgetSettingsContext
): SettingsFields {
    const choices = new Map<string, readonly string[]>();
    const defaults = new Map<string, SettingsFieldValue>();
    const fields = fieldsForObject(schema, ['widgetConfig'], {
        cell: context,
        output: { choices, defaults },
    });
    return { fields, choices, defaults };
}

function fieldsForObject(
    schema: ZodMiniType,
    path: string[],
    context: FieldsContext,
    skip?: string
): EditableOption[] {
    const shape = schemaDef(baseSchemaOf(schema)).shape ?? {};
    return Object.entries(shape).flatMap(([key, field]) =>
        key === skip ? [] : fieldsForKey(key, field, path, context)
    );
}

function fieldsForKey(
    key: string,
    field: ZodMiniType,
    path: string[],
    context: FieldsContext,
    labelOverride?: string
): EditableOption[] {
    let meta: DashboardWidgetFieldMeta = {};
    for (let node: ZodMiniType | undefined = field; node; node = schemaDef(node).innerType) {
        const registered = fieldMeta.get(node);
        if (registered !== undefined) {
            meta = registered;
            break;
        }
    }

    const propertyPath = [...path, key];
    const name = labelOverride ?? meta.label ?? key;

    const repeat = repeatedMeta.get(field);
    if (repeat !== undefined) {
        return repeatedFields(repeat, propertyPath, name, context);
    }

    const columnMeta = perColumnMeta.get(field);
    if (columnMeta !== undefined) {
        return perColumnFields(columnMeta, propertyPath, name, context);
    }

    const inner = baseSchemaOf(field);
    const innerDef = schemaDef(inner);
    if (innerDef.shape !== undefined) {
        return meta.inline === true
            ? fieldsForObject(inner, propertyPath, context)
            : [groupField(inner, propertyPath, name, context)];
    }

    // A dropdown offers its own values.
    let values = meta.choices?.(context.cell);
    if (values === undefined && innerDef.entries !== undefined) {
        values = Object.values(innerDef.entries);
    }

    // The panel can only edit text, numbers and switches, plus anything offering a list to pick from.
    const editable =
        innerDef.type === 'string' || innerDef.type === 'number' || innerDef.type === 'boolean';
    if (values === undefined && !editable) {
        return [];
    }

    let control: ElementType = 'input';
    if (values !== undefined) {
        control = 'select';
        context.output.choices.set(propertyPath.join('.'), values);
    } else if (innerDef.type === 'boolean') {
        control = 'toggle';
    } else if (innerDef.type === 'string' && meta.multiline === true) {
        control = 'textarea';
    }

    return [
        {
            name,
            type: control,
            propertyPath,
            ...(values !== undefined && {
                selectOptions: values.map((value) => ({ name: value })),
            }),
        },
    ];
}

/** One card holding the count dropdown, then a collapsible group per item the count asks for. */
function repeatedFields(
    repeat: RepeatedMeta,
    path: string[],
    name: string,
    context: FieldsContext
): EditableOption[] {
    let stored = context.cell.config;
    for (const step of path.slice(1)) {
        stored = isRecord(stored) ? stored[step] : undefined;
    }
    const countPath = [...path, 'count'];
    context.output.choices.set(countPath.join('.'), repeat.counts);
    const storedCount = isRecord(stored) ? stored['count'] : undefined;
    const asked = Number(storedCount ?? repeat.fallbackCount);
    const shown = Number.isFinite(asked) ? repeat.keys.slice(0, asked) : [];

    const countField: EditableOption = {
        name: repeat.countLabel,
        type: 'select',
        propertyPath: countPath,
        selectOptions: repeat.counts.map((count) => ({ name: count })),
    };
    const itemCards = shown.map((key, index): EditableOption => ({
        name: repeat.itemLabel,
        type: 'nested',
        nestedOptions: [
            {
                name: repeat.itemLabel,
                nameParams: { index: index + 1 },
                options: fieldsForObject(repeat.item, [...path, 'items', key], context),
            },
        ],
    }));

    return [
        {
            name,
            type: 'nested',
            isStandalone: true,
            nestedOptions: [{ name, options: [countField, ...itemCards] }],
        },
    ];
}

/** One collapsible card, holding every column's fields. */
function perColumnFields(
    { item, columns }: PerColumnMeta,
    path: string[],
    name: string,
    context: FieldsContext
): EditableOption[] {
    // One schema serves every column, so each field takes its label from the column it draws.
    const fields = columns(context.cell).flatMap((column) => {
        for (const [field, value] of Object.entries(column.defaults ?? {})) {
            context.output.defaults.set([...path, column.key, field].join('.'), value);
        }
        return fieldsForKey(column.key, item, path, context, column.label);
    });
    // With no columns, or an item the panel cannot draw, the card would be empty.
    if (fields.length === 0) {
        return [];
    }
    return [
        { name, type: 'nested', isStandalone: true, nestedOptions: [{ name, options: fields }] },
    ];
}

function groupField(
    schema: ZodMiniType,
    path: string[],
    name: string,
    context: FieldsContext
): EditableOption {
    const flag = schemaDef(schema).shape?.['enabled'];
    const toggled = flag !== undefined && schemaDef(baseSchemaOf(flag)).type === 'boolean';
    return {
        name,
        type: 'nested',
        // The panel gives every top level setting a card of its own, so a card that draws its own
        // header would show the same title twice, one inside the other. `isStandalone` drops the
        // outer one. Deeper down the panel never adds that card, so this changes nothing there.
        isStandalone: true,
        nestedOptions: [
            {
                name,
                ...(toggled && { showToggle: true, propertyPath: [...path, 'enabled'] }),
                options: fieldsForObject(schema, path, context, toggled ? 'enabled' : undefined),
            },
        ],
    };
}

/** The schema under the wrappers */
function baseSchemaOf(schema: ZodMiniType): ZodMiniType {
    let current = schema;
    let inner = schemaDef(current).innerType;
    while (inner !== undefined) {
        current = inner;
        inner = schemaDef(current).innerType;
    }
    return current;
}
